import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import { queryKeys } from "@/api/query-keys";
import type { PaginatedResponse } from "@/api/types";
import { api } from "@/lib/api";
import type {
	CreatedDistrict,
	CreatedDistrictManager,
	CreatedDistrictsParams,
	DistrictManagerDetail,
	ManagersParams,
	MqttMeterReading,
	MqttMeterReadingsParams,
	MqttReadingDetailParams,
	MqttReadingsParams,
	MqttSource,
	MqttSourceDetail,
	MqttSourcesParams,
	Reading,
	ReadingDetail,
	ResellerDashboard,
	ResellerDashboardParams,
	ResellerDistrictDetail,
	ResellerMyProfile,
	ResellerProfile,
} from "./types";

export function useResellerDashboard(
	params: ResellerDashboardParams = {},
): UseQueryResult<ResellerDashboard, Error> {
	return useQuery({
		queryKey: queryKeys.reseller.dashboard(params),
		queryFn: async (): Promise<ResellerDashboard> => {
			const res = await api.get<ResellerDashboard>("/api/reseller/dashboard/", {
				params,
			});
			return res.data;
		},
	});
}

export function useCreatedDistrictManagers(
	params: ManagersParams = {},
): UseQueryResult<PaginatedResponse<CreatedDistrictManager>, Error> {
	return useQuery({
		queryKey: queryKeys.reseller.managers(params),
		queryFn: async (): Promise<PaginatedResponse<CreatedDistrictManager>> => {
			const res = await api.get<PaginatedResponse<CreatedDistrictManager>>(
				"/api/reseller/created_district_managers/",
				{ params },
			);
			return res.data;
		},
	});
}

export function useResellerDetailMe(): UseQueryResult<ResellerProfile, Error> {
	return useQuery({
		queryKey: queryKeys.reseller.detailMe(),
		queryFn: async (): Promise<ResellerProfile> => {
			const res = await api.get<ResellerProfile>("/api/reseller/detail_me/");
			return res.data;
		},
	});
}

export function useResellerMqttSources(
	params: MqttSourcesParams = {},
): UseQueryResult<PaginatedResponse<MqttSource>, Error> {
	return useQuery({
		queryKey: queryKeys.reseller.mqttSources(params),
		queryFn: async (): Promise<PaginatedResponse<MqttSource>> => {
			const res = await api.get<PaginatedResponse<MqttSource>>(
				"/api/reseller/mqtt_sources/",
				{ params },
			);
			return res.data;
		},
	});
}

export function useDistrictManager(
	id: number,
): UseQueryResult<DistrictManagerDetail, Error> {
	return useQuery({
		queryKey: queryKeys.reseller.manager(id),
		queryFn: async (): Promise<DistrictManagerDetail> => {
			const res = await api.get<DistrictManagerDetail>(`/api/reseller/${id}/`);
			return res.data;
		},
		enabled: !!id,
	});
}

export function useCreatedDistricts(
	params: CreatedDistrictsParams = {},
): UseQueryResult<PaginatedResponse<CreatedDistrict>, Error> {
	return useQuery({
		queryKey: queryKeys.reseller.createdDistricts(params),
		queryFn: async (): Promise<PaginatedResponse<CreatedDistrict>> => {
			const res = await api.get<PaginatedResponse<CreatedDistrict>>(
				"/api/reseller/created_districts/",
				{ params },
			);
			return res.data;
		},
	});
}

export function useResellerMyProfile(): UseQueryResult<
	ResellerMyProfile,
	Error
> {
	return useQuery({
		queryKey: queryKeys.reseller.myProfile(),
		queryFn: async (): Promise<ResellerMyProfile> => {
			const res = await api.get<ResellerMyProfile>("/api/reseller/my_profile/");
			return res.data;
		},
	});
}

export function useResellerMqttSourceDetail(
	sn: string,
): UseQueryResult<MqttSourceDetail, Error> {
	return useQuery({
		queryKey: queryKeys.reseller.mqttSource(sn),
		queryFn: async (): Promise<MqttSourceDetail> => {
			const res = await api.get<MqttSourceDetail>(
				`/api/reseller/mqtt_sources/${sn}/`,
			);
			return res.data;
		},
		enabled: !!sn,
	});
}

export function useResellerDistrictDetail(
	districtId: number,
): UseQueryResult<ResellerDistrictDetail, Error> {
	return useQuery({
		queryKey: queryKeys.reseller.districtDetail(districtId),
		queryFn: async (): Promise<ResellerDistrictDetail> => {
			const res = await api.get<ResellerDistrictDetail>(
				`/api/reseller/districts/${districtId}/`,
			);
			return res.data;
		},
		enabled: !!districtId,
	});
}

export function useReadings(
	params: MqttReadingsParams = {},
): UseQueryResult<PaginatedResponse<Reading>, Error> {
	return useQuery({
		queryKey: queryKeys.reseller.readings(params),
		queryFn: async (): Promise<PaginatedResponse<Reading>> => {
			const res = await api.get<PaginatedResponse<Reading>>(
				"/api/reseller/readings/",
				{ params },
			);
			return res.data;
		},
	});
}

export function useReadingDetail(
	readingId: number,
	params: MqttReadingDetailParams = {},
	options: { enabled?: boolean } = {},
): UseQueryResult<ReadingDetail, Error> {
	return useQuery({
		queryKey: [...queryKeys.reseller.readingDetail(readingId), params],
		queryFn: async (): Promise<ReadingDetail> => {
			const res = await api.get<ReadingDetail>(
				`/api/reseller/readings/${readingId}/`,
				{ params },
			);
			return res.data;
		},
		enabled: !!readingId && (options.enabled ?? true),
	});
}

export function useMqttMeterReadings(
	districtId: number,
	mqttReadingId: number,
	params: MqttMeterReadingsParams = {},
): UseQueryResult<PaginatedResponse<MqttMeterReading>, Error> {
	return useQuery({
		queryKey: queryKeys.reseller.mqttMeterReadings(
			districtId,
			mqttReadingId,
			params,
		),
		queryFn: async (): Promise<PaginatedResponse<MqttMeterReading>> => {
			const res = await api.get<PaginatedResponse<MqttMeterReading>>(
				`/api/reseller/districts/${districtId}/mqtt-readings/${mqttReadingId}/meter-readings/`,
				{ params },
			);
			return res.data;
		},
		enabled: !!districtId && !!mqttReadingId,
	});
}
